import { Fragment } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import type { LoadOperation } from "@/types";
import { FieldConfig, OPERATION_COLORS } from "./config";
import { buildRecordSummary, SecondaryItem } from "./buildRecordSummary";
import type { RowAttention } from "./loadResultModel";

interface LoadedRecordRowProps {
  entity: Record<string, unknown> | null;
  operation: LoadOperation;
  fields: FieldConfig[];
  claimantLookup?: Record<string, string>;
  /** Click destination — typically `/${resource}/${id}/show`. When the entity
   *  is missing (deleted, soft-deleted), the row renders non-clickable. */
  to?: string;
  /** True when the entity ID was in the load result but the fetch returned
   *  no record — usually a soft-deleted entity. Renders a muted "missing"
   *  treatment. */
  isMissing?: boolean;
  /** A load warning about this row (held spend, skipped totals): shown as a
   *  warning-tone caption under the summary instead of a page-wide banner. */
  attention?: RowAttention;
}

/**
 * Single row in a `LoadedRecordsList`. Replaces a row in the legacy
 * `PaginatedDatagrid` — the chunky multi-column grid with dashes for empty
 * fields that surfaced as the "Loaded Records looks amateur" complaint.
 *
 * Row anatomy: leading operation chip · primary text (+ optional secondary
 * line built from populated fields) · trailing chevron. Whole row is
 * clickable. The `buildRecordSummary` helper enforces empty-field
 * suppression so the secondary line only contains fields with real data.
 */
export const LoadedRecordRow = ({
  entity,
  operation,
  fields,
  claimantLookup,
  to,
  isMissing = false,
  attention,
}: LoadedRecordRowProps) => {
  const summary = buildRecordSummary(entity, fields, claimantLookup);
  const operationLabel = operation.charAt(0).toUpperCase() + operation.slice(1);
  const clickable = !!to && !isMissing;

  const rowContent = (
    <>
      <Chip
        label={operationLabel}
        size="small"
        color={OPERATION_COLORS[operation]}
        variant="outlined"
        sx={{ flexShrink: 0, fontWeight: 500, minWidth: 84, justifyContent: "center" }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isMissing ? (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            Record not found
          </Typography>
        ) : (
          <>
            <Typography variant="body2" fontWeight={500} noWrap title={summary.primary}>
              {summary.primary}
            </Typography>
            {summary.secondary.length > 0 && <SecondaryLine items={summary.secondary} />}
            {attention && (
              <Typography
                variant="caption"
                component="div"
                title={attention.detail}
                sx={{ color: "warning.dark", lineHeight: 1.4 }}
                data-testid="loaded-row-attention"
              >
                {attention.text}
              </Typography>
            )}
          </>
        )}
      </Box>
      {clickable && <ChevronRightIcon sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0 }} />}
    </>
  );

  const sharedSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    py: 1.25,
    px: 1.5,
    borderBottom: 1,
    borderColor: "divider",
    "&:last-of-type": { borderBottom: 0 },
  };

  if (!clickable) {
    return <Box sx={sharedSx}>{rowContent}</Box>;
  }

  return (
    <Box
      component={RouterLink}
      to={to!}
      sx={{
        ...sharedSx,
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
        transition: "background-color 120ms ease",
        "&:hover": { bgcolor: "action.hover" },
        "&:focus-visible": { outline: 2, outlineColor: "primary.main", outlineOffset: -2 },
      }}
    >
      {rowContent}
    </Box>
  );
};

/**
 * Renders the secondary line as `label value · label value · …`. Labels are
 * muted so the value stays the visual anchor; reviewers asked for the labels
 * so a bare value list like "$813.00 · $813.00 · 03/12/26" wouldn't read as
 * "Amount · Requested · Received."
 */
const SecondaryLine = ({ items }: { items: SecondaryItem[] }) => (
  <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.4 }}>
    {items.map((item, i) => (
      <Fragment key={`${item.label}-${i}`}>
        {i > 0 && (
          <Box component="span" sx={{ mx: 0.75, color: "text.disabled" }}>
            ·
          </Box>
        )}
        <Box component="span" sx={{ opacity: 0.7, mr: 0.5 }}>
          {item.label}
        </Box>
        <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
          {item.value}
        </Box>
      </Fragment>
    ))}
  </Typography>
);
